import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text/text';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {Pill} from '@Components/pill/pill';
import {PillSize, PillType} from '@Components/pill/pill.types';
import {getComponentAction} from '@Components/ai-chat-window/lib/component-actions/component-action-registry';
import {useComponentActionEntries} from '@Components/ai-chat-window/lib/component-actions/component-action.hooks';
import type {ActionListProps, ActionListRow} from './action-list.types';
import styles from './action-list.module.scss';

export function ActionList({rows, actionType}: ActionListProps): ReactElement {
  const action = getComponentAction(actionType);
  const {doneTargetIds, startAction} = useComponentActionEntries(action, rows);

  const renderRowIcon = (row: ActionListRow): ReactElement | null => {
    if (row.iconUrl === undefined || row.iconUrl === '') {
      return null;
    }

    return <img className={styles.actionRowIcon} src={row.iconUrl} alt="" aria-hidden="true" />;
  };

  const renderRowMeta = (row: ActionListRow): ReactElement | null => {
    if (row.meta === undefined || row.meta === '') {
      return null;
    }

    return <Text val={row.meta} size={TextSize.MD} className={styles.actionRowMeta} />;
  };

  const renderRowAction = (row: ActionListRow): ReactElement | null => {
    if (action === undefined) {
      return null;
    }

    if (doneTargetIds.includes(row.id)) {
      return <Pill label={window.i18next.t(action.doneLabelKey)} type={PillType.SUCCESS} size={PillSize.SM} />;
    }

    return (
      <Button
        text={row.actionLabel}
        type={ButtonType.SECONDARY}
        size={ButtonSize.SMALL}
        customClasses="radius-8"
        textClasses="_text-transform-none"
        attributes={{'aria-label': `${row.actionLabel}: ${row.label}`}}
        onClick={(): void => {
          startAction(row);
        }}
      />
    );
  };

  return (
    <div className={styles.actionList}>
      <ul className={styles.actionRowList} role="list">
        {rows.map((row) => (
          <li key={row.id} className={styles.actionRow}>
            {renderRowIcon(row)}
            <span className={styles.actionRowText}>
              <Text val={row.label} size={TextSize.MD} bold className={styles.actionRowLabel} />
              {renderRowMeta(row)}
            </span>
            {renderRowAction(row)}
          </li>
        ))}
      </ul>
    </div>
  );
}
